/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState, useRef, useCallback } from 'react';
import * as S from './Table.styled';
import { getTranslated, TranslateKey, Translates } from 'messages';
import { makeArray, toggleInArray } from 'utils/arrays';
import Skeleton from 'components/Skeleton/Skeleton';
import {
    ColumnOptions,
    ColumnProps,
    IdProps,
    ParseTableColumns,
    RowFormattedProps,
    TableProps,
    HeadingCellProps
} from './Table.types';
import { getColumnKey, isColumnOptions } from './utils';
import { getIn } from 'utils/getIn';
import { namesParseColumns, typesParseColumns } from './Table.const';
import { useSetFalse, useToggle } from 'hooks/booleans';
import { useClickOutside } from 'hooks/useClickOutside';
import { Calendar } from 'components/Calendar/Calendar';
import { CalendarBaseContainer } from 'components/Calendar/Calendar.styled';
import { useMaxWidth } from 'hooks/useMaxWidth';
import { RawSelect } from 'ui/Select';
import { isNil } from 'utils/nulls';
import Icon from 'ui/Icon/Icon';

export function HeadingCell<
    RowData extends IdProps<IdValue>,
    IdColumn extends keyof RowData = 'id',
    IdValue extends RowData[IdColumn] = RowData[IdColumn],
    FiltersValues extends Record<string, unknown> = Record<string, unknown>
>({
    value,
    index,
    editMode,
    filtersToColumns,
    name,
    filtersValues,
    onFiltersChange
}: HeadingCellProps<RowData, IdColumn, IdValue, FiltersValues>) {
    const [showFilter, setShowFilter] = useState(false);
    const toggleFilter = useToggle(setShowFilter);
    const closeFilter = useSetFalse(setShowFilter);

    const options = isColumnOptions(value) ? value : null;

    const content = options ? options.title : String(value);

    const filter = filtersToColumns?.[name];

    const labelRef = useRef<HTMLDivElement>(null);
    const filterRef = useClickOutside(closeFilter, [labelRef], showFilter);

    const filterValue = filter && filtersValues?.[filter.key];

    const clearFilter = useCallback(() => {
        if (!filter) return;

        onFiltersChange?.({
            ...(filtersValues || {}),
            [filter.key]: undefined
        } as FiltersValues);
    }, [filter, filtersValues, onFiltersChange]);

    const filterContent = useMemo(() => {
        if (!filter) return null;

        const onChange = (value: any) => {
            onFiltersChange?.({
                ...(filtersValues || {}),
                [filter.key]: value
            } as FiltersValues);

            if (filter.component === 'select' && !filter.props.multiple) {
                closeFilter();
            }
        };

        switch (filter.component) {
            case 'date':
                return (
                    <CalendarBaseContainer>
                        <Calendar
                            value={filterValue as string}
                            selectRange
                            onChange={onChange}
                        />
                    </CalendarBaseContainer>
                );
            case 'select':
                return (
                    <S.FilterSelectContainer>
                        <RawSelect<any>
                            value={filterValue as any}
                            onChange={onChange}
                            defaultOpened
                            hideMeta
                            {...filter.props}
                        />
                    </S.FilterSelectContainer>
                );
            default:
                return null;
        }
    }, [closeFilter, filter, filterValue, filtersValues, onFiltersChange]);

    return (
        <S.HeadingCell
            $editMode={index === 0 && editMode}
            className={`th-${getColumnKey(name as string)}`}
        >
            <S.HeadingCellContainer>
                {!!content && (
                    <S.HeadingCellContent
                        onClick={toggleFilter}
                        $filter={!!filter}
                        $activeFilter={!isNil(filterValue)}
                        ref={labelRef}
                    >
                        {content}
                    </S.HeadingCellContent>
                )}
                <S.DeleteFilterContainer
                    onClick={clearFilter}
                    $active={!isNil(filterValue)}
                >
                    <Icon size="s" fill="white" icon="close" />
                </S.DeleteFilterContainer>

                {!!filter && (
                    <S.FilterContainer $show={showFilter}>
                        <S.FilterInner ref={filterRef}>
                            {filterContent}
                        </S.FilterInner>
                    </S.FilterContainer>
                )}
            </S.HeadingCellContainer>
        </S.HeadingCell>
    );
}

export function Table<
    RowData extends IdProps<IdValue, EnabledColumn>,
    Columns extends ColumnProps<RowData> = ColumnProps<RowData>,
    IdColumn extends keyof RowData = 'id',
    IdValue extends RowData[IdColumn] = RowData[IdColumn],
    FiltersValues extends Record<string, unknown> = Record<string, unknown>,
    EnabledColumn extends keyof RowData | undefined = undefined
>({
    columns,
    rows: rawRows,
    showHead = true,
    parseColumns: rawParseColumns,
    translateColumns,
    noRowsMsg = 'Нет данных',
    loading,
    idColumn = 'id' as any,
    linkGetter,
    paddingTop,
    afterRowContent,
    beforeRowContent,
    onRowSelect,
    selectedRows,
    editMode,
    disableScroll,
    filters,
    filtersValues,
    onFiltersChange,
    enabledColumn,
    disabledHint
}: TableProps<
    RowData,
    Columns,
    IdColumn,
    IdValue,
    FiltersValues,
    EnabledColumn
>): JSX.Element {
    const { maxWidth, ref } = useMaxWidth();

    const parseColumns: ParseTableColumns<RowData, IdColumn, IdValue> = useMemo(
        () => ({
            ...namesParseColumns,
            ...rawParseColumns
        }),
        [rawParseColumns]
    );

    const filtersToColumns = useMemo(() => {
        return (
            filters &&
            Object.keys(filters).reduce((acc, key) => {
                const column = filters[key].column;

                return {
                    ...acc,
                    [column]: { ...filters[key], key }
                };
            }, {} as any)
        );
    }, [filters]);

    const rows = useMemo(
        () =>
            rawRows?.map((row, index) => {
                const { link, ...formattedRow } = Object.keys(columns).reduce<
                    RowFormattedProps<RowData>
                >((prev, rawKey) => {
                    const key = rawKey as keyof Columns;
                    const value = getIn(row, rawKey as Paths<RowData>);

                    const columnOptions =
                        typeof columns[key] === 'object'
                            ? (columns[key] as ColumnOptions<RowData, any>)
                            : undefined;

                    const translateGroup: TranslateKey | undefined =
                        columnOptions?.translateGroup ??
                        translateColumns?.[key];

                    const formattedValue = translateGroup
                        ? makeArray(value).map((val) => (
                              <>
                                  <S.CellListValue key={rawKey}>
                                      {getTranslated(
                                          translateGroup,
                                          val as keyof Translates[TranslateKey]
                                      )}
                                  </S.CellListValue>
                              </>
                          ))
                        : value;

                    return {
                        ...prev,
                        [key]:
                            (columnOptions?.parse ?? parseColumns?.[key])?.(
                                formattedValue as DeepIdx<RowData, any>,
                                row,
                                value as DeepIdx<RowData, any>
                            ) ||
                            (columnOptions?.type
                                ? typesParseColumns[columnOptions.type]?.(
                                      formattedValue
                                  )
                                : formattedValue)
                    };
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                }, {} as any);

                const id = row[idColumn] as IdValue;

                const { onClick } = row;

                const isEnabled = enabledColumn ? !!row[enabledColumn] : true;

                const handleClick = () => {
                    onClick?.();
                    if (editMode && isEnabled) {
                        onRowSelect?.(
                            toggleInArray(id, selectedRows ?? []),
                            id
                        );
                    }
                };

                const finalLink = link || linkGetter?.(id, row);

                const contentWithLink = (() => {
                    const content = (
                        <S.Row
                            onClick={handleClick}
                            $hasLink={!!finalLink}
                            $editMode={editMode}
                            $enabled={isEnabled}
                            key={id as string}
                            title={!isEnabled ? disabledHint : undefined}
                        >
                            {beforeRowContent?.(id, row)}

                            {Object.keys(columns).map((key: any, index) => (
                                <S.Cell
                                    className={`td-${getColumnKey(key)}`}
                                    data-key={key}
                                    key={key}
                                >
                                    {editMode && index === 0 && (
                                        <S.CheckboxContainer>
                                            {isEnabled && (
                                                <S.Checkbox
                                                    $active={
                                                        selectedRows?.includes(
                                                            id
                                                        ) ?? false
                                                    }
                                                />
                                            )}
                                        </S.CheckboxContainer>
                                    )}

                                    <S.CellContent>
                                        {(formattedRow as any)[key]}
                                    </S.CellContent>
                                </S.Cell>
                            ))}
                        </S.Row>
                    );

                    return finalLink ? (
                        <S.RowLink to={finalLink}>{content}</S.RowLink>
                    ) : (
                        content
                    );
                })();

                return (
                    <S.RowContainer key={index}>
                        {contentWithLink}
                        {afterRowContent?.(row[idColumn] as IdValue, row)}
                    </S.RowContainer>
                );
            }),
        [
            rawRows,
            columns,
            idColumn,
            enabledColumn,
            linkGetter,
            afterRowContent,
            translateColumns,
            parseColumns,
            editMode,
            onRowSelect,
            selectedRows,
            disabledHint,
            beforeRowContent
        ]
    );

    return (
        <>
            {loading ? (
                <Skeleton />
            ) : (
                <S.Container
                    $columns={columns as any}
                    $paddingTop={paddingTop}
                    $disableScroll={disableScroll}
                    $maxWidth={maxWidth}
                    ref={ref}
                >
                    {showHead && (
                        <S.Head>
                            <S.HeadRow>
                                {Object.entries(columns).map(
                                    ([key, value], index) => (
                                        <HeadingCell
                                            key={key}
                                            name={key}
                                            value={value as any}
                                            index={index}
                                            editMode={editMode}
                                            filtersToColumns={filtersToColumns}
                                            filtersValues={filtersValues}
                                            onFiltersChange={onFiltersChange}
                                        />
                                    )
                                )}
                            </S.HeadRow>
                        </S.Head>
                    )}
                    {rows?.length ? (
                        <S.Body>{rows}</S.Body>
                    ) : (
                        <S.NoRowsContainer>{noRowsMsg}</S.NoRowsContainer>
                    )}
                </S.Container>
            )}
        </>
    );
}
